import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AbcConfigService } from '@barthauer/abc/core/configuration';
import { Observable, of, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { ProfileSelectionComponentInfo } from './../../../authentication/src/lib/profile-selection/profile-selection-component-info';
import { AbcCoreDataModule } from './abc-core-data.module';
import { CacheService } from './caching/cache.service';
import { ObjectTypeInfo } from './models/objectTypeInfo.model';
import { ObjectTypeInfoViewModel } from './viewModels/objectTypeInfo.viewModel';

@Injectable({
  providedIn: AbcCoreDataModule,
})
export class ObjectSpaceService {
  private objectTypeInfosServiceUri: string;
  private allInfoObjects: ObjectTypeInfo[];
  private allInfoViewObjectsByName;
  private allInfoViewObjectsByBaseType;
  private allInfoViewObjectsByChildType;
  private getObjectTypeSubjectsBeforeInit: Subject<ObjectTypeInfoViewModel>[];

  constructor(configService: AbcConfigService, private http: HttpClient, private cacheService: CacheService) {
    this.objectTypeInfosServiceUri = configService.configuration.WebApi.ServiceURLs.ObjectTypesInfo;
    this.getObjectTypeSubjectsBeforeInit = new Array<Subject<ObjectTypeInfoViewModel>>();
  }

  private _getObjectType(name: string | number): ObjectTypeInfoViewModel {
    if (!name) {
      return null;
    }
    let ot: string;

    let result = this.allInfoViewObjectsByName[name];
    if (!result) {
      let infoObject;
      if (typeof name === 'number') {
        infoObject = this.allInfoObjects.find((infoObj) => infoObj.Value === name.toString());
        ot = infoObject?.Name;
      } else {
        infoObject = this.allInfoObjects.find((infoObj) => infoObj.Name === name);
        if (!infoObject) {
          infoObject = this.allInfoObjects.find((infoObj) => infoObj.Value === name);
          ot = infoObject?.Name;
        } else {
          ot = name;
        }
      }
      if (infoObject) {
        result = new ObjectTypeInfoViewModel(
          infoObject,
          (ot) => this._getObjectType(ot),
          () => {
            this.indexAllObjectTypesByBaseType();
            return this.allInfoViewObjectsByBaseType;
          },
          () => {
            this.indexAllObjectTypesByChildType();
            return this.allInfoViewObjectsByChildType;
          }
        );
        this.allInfoViewObjectsByName[ot] = result;
      }
    }
    return result;
  }

  getObjectType(name: string | number): Observable<ObjectTypeInfoViewModel> {
    if (this.allInfoObjects) {
      return of(this._getObjectType(name));
    } else {
      const initSubject$ = new Subject<ObjectTypeInfoViewModel>();
      this.getObjectTypeSubjectsBeforeInit.push(initSubject$);
      return initSubject$.asObservable().pipe(map(() => this._getObjectType(name)));
    }
  }

  private indexAllObjectTypesByBaseType() {
    if (!this.allInfoViewObjectsByBaseType) {
      this.allInfoViewObjectsByBaseType = {};
      for (const infoObject of this.allInfoObjects) {
        if (infoObject.BaseTypeName) {
          if (!this.allInfoViewObjectsByBaseType[infoObject.BaseTypeName]) {
            this.allInfoViewObjectsByBaseType[infoObject.BaseTypeName] = new Array<ObjectTypeInfoViewModel>();
          }
          this.allInfoViewObjectsByBaseType[infoObject.BaseTypeName].push(this._getObjectType(infoObject.Name));
        }
      }
    }
  }

  private indexAllObjectTypesByChildType() {
    if (!this.allInfoViewObjectsByChildType) {
      this.allInfoViewObjectsByChildType = {};
      for (const infoObject of this.allInfoObjects) {
        const referencing = infoObject.Referencing;
        if (referencing) {
          for (let i = 0; i < referencing.length; i++) {
            if (referencing[i].ReferencingColumn === 'ParentId') {
              if (!this.allInfoViewObjectsByChildType[referencing[i].ReferencingObjectTypeName]) {
                this.allInfoViewObjectsByChildType[referencing[i].ReferencingObjectTypeName] =
                  new Array<ObjectTypeInfoViewModel>();
              }
              this.allInfoViewObjectsByChildType[referencing[i].ReferencingObjectTypeName].push(
                this._getObjectType(infoObject.Name)
              );
            }
          }
        }
      }
      for (const key in this.allInfoViewObjectsByChildType) {
        if (Object.prototype.hasOwnProperty.call(this.allInfoViewObjectsByChildType, key)) {
          const elementsToCopy: Map<string, ObjectTypeInfoViewModel> = new Map();
          let elements: Array<ObjectTypeInfoViewModel> = this.allInfoViewObjectsByChildType[key];
          for (let i = 0; i < elements.length; i++) {
            if (elements[i].baseType) {
              const index = elements.findIndex((otvm) => (otvm = elements[i].baseType));
              if (index === -1) {
                elementsToCopy.set(elements[i].name, elements[i]);
              }
            } else {
              elementsToCopy.set(elements[i].name, elements[i]);
            }
          }
          this.allInfoViewObjectsByChildType[key] = elementsToCopy;
        }
      }
    }
  }

  private getObjectTypesInfo(): Observable<Object> {
    const query: object = { OutputMode: 'Json' };
    return this.cacheService.httpPostObject(this.http, this.objectTypeInfosServiceUri, query);
  }

  private _isInitialized: boolean;
  public get isInitialized(): boolean {
    return this._isInitialized;
  }

  public initialize(): Promise<void> {
    this._isInitialized = false;
    const subject: Subject<void> = new Subject();
    this.getObjectTypesInfo().subscribe({
      next: (objectTypes) => {
        const arrData: object[] = objectTypes as any;
        const infos: Array<ObjectTypeInfo> = new Array<ObjectTypeInfo>();
        for (let i = 0; i < arrData.length; i++) {
          infos.push(new ObjectTypeInfo(arrData[i]));
        }
        this.allInfoViewObjectsByName = {};
        this.allInfoObjects = infos;

        for (const getObjectTypeSubjectBeforeInit$ of this.getObjectTypeSubjectsBeforeInit) {
          getObjectTypeSubjectBeforeInit$.next(null);
          getObjectTypeSubjectBeforeInit$.complete();
        }
      },
      error: (err) => {
        if (
          err instanceof HttpErrorResponse &&
          (err.status === 401 || err.status === 403 || ProfileSelectionComponentInfo.hasProfileNotFoundMessage(err))
        ) {
          console.error('ObjectSpace Init failed due to missing login and profile data.', err);
        } else {
          // this.abcCore.error.publish(err);
        }
      },
      complete: () => {
        this._isInitialized = true;
        subject.next(null);
        subject.complete();
      },
    });
    return subject.asObservable().toPromise();
  }

  public ensureInitialized() {
    if (!this.isInitialized) {
      this.initialize();
    }
  }

  public getAllObjectTypesInfo(): ObjectTypeInfo[] {
    return this.allInfoObjects;
  }

  public async isMatch(name1: string | number, name2: string | number): Promise<boolean> {
    const ot1 = await this.getObjectType(name1).toPromise();
    return ot1?.isMatchOrBase(name2);
  }

  public async is(candidate: string | number, nameOrBaseName: string | number): Promise<boolean> {
    const otCandidate = await this.getObjectType(candidate).toPromise();
    return otCandidate?.isMatchOrBase(nameOrBaseName);
  }
}
